import { ContentState, RawDraftContentState } from 'draft-js';
import { IntlShape, MessageDescriptor } from 'react-intl';
import * as Yup from 'yup';

import { SG_PROVINCE_LOCATION } from '../../../common/constants';
import {
  countryCodeIsIndonesia,
  countryCodeIsMalaysia,
  countryCodeIsSingapore,
} from '../../../common/helpers/countryCodeHelper';
import { getFeatureEnabledForCountry } from '../../../common/hooks/useGetFeatureEnabledForCountry';
import { getFirstForbiddenKeywordByCountry } from '../../../common/validationHelper';
import { WorkAddressFormValues } from '../../../components/Modal/WorkAddressModal/types';
import { getConfig } from '../../../config';
import { COMPANY_SIZES } from '../SignUpPage/constants';
import { errorMessages, messages } from './companyCreationMessages';
import { isLocationCompatibleWithCountry } from './helpers';

export const COMPANY_CREATION_STAGE = {
  COMPANY_NAME: 'COMPANY_NAME',
  COMPANY_FORM: 'COMPANY_FORM',
  USER_WHATSAPP_VERIFICATION: 'USER_WHATSAPP_VERIFICATION',
};

export type SelectedLocation = {
  id: string;
  countryCode?: string | null;
};

export interface CompanyFormState {
  name: string;
  logo: string;
  website?: string;
  brandName?: string;
  size: (typeof COMPANY_SIZES)[keyof typeof COMPANY_SIZES];
  description: ContentState;
  descriptionRaw: RawDraftContentState;
  IndustryId: string;
  CountryCode: string;
  locationProvinceId?: string;
  locationCityId?: string;
  locationDistrictId?: string;
  selectedLocation?: SelectedLocation | null;
  CityId?: number;
  labelForCityId?: string;
  LocationId?: number;
  address?: string;
  acquisitionReferralCode?: string;
  stage: (typeof COMPANY_CREATION_STAGE)[keyof typeof COMPANY_CREATION_STAGE];
  companyExistsError?: string;
  brandNameExistsError?: string;
  workAddress?: WorkAddressFormValues;
}

export const getDefaultLocationProvinceId = (countryCode?: string) => {
  if (countryCodeIsSingapore(countryCode ?? getConfig().COUNTRY)) {
    return SG_PROVINCE_LOCATION.id;
  }
  return undefined;
};

export const initialValues: Partial<CompanyFormState> = {
  name: '',
  logo: '',
  website: '',
  size: COMPANY_SIZES.BETWEEN_11_AND_50,
  IndustryId: '',
  CountryCode: getConfig().COUNTRY?.toUpperCase(),
  locationProvinceId: getDefaultLocationProvinceId(),
  locationCityId: undefined,
  selectedLocation: null,
  CityId: undefined,
  LocationId: undefined,
  brandName: undefined,
  companyExistsError: undefined,
  brandNameExistsError: undefined,
  address: '',
  stage: COMPANY_CREATION_STAGE.COMPANY_NAME,
};

export const referralCodeRegex = new RegExp('^GLVN-\\d{3}$');
const MIN_COMPANY_NAME_LENGTH = 3;
function testForForbiddenKeywords({
  fieldName,
  formatMessage,
  stringSchema,
  path,
}: {
  formatMessage: IntlShape['formatMessage'];
  stringSchema: Yup.StringSchema;
  fieldName: MessageDescriptor;
  path: string;
}) {
  return stringSchema.test({
    name: 'noForbiddenKeywords',
    test: function (this: any, name: any) {
      const firstForbiddenWord = getFirstForbiddenKeywordByCountry(
        name,
        (getConfig().COUNTRY as string).toUpperCase(),
        false
      );
      return (
        (firstForbiddenWord &&
          this.createError({
            message: formatMessage(errorMessages.hasForbiddenKeywords, {
              field: formatMessage(fieldName),
              keyword: firstForbiddenWord,
            }),
            path: path,
          })) ||
        true
      );
    },
  });
}
export const companyFormValidationSchema = (
  formatMessage: IntlShape['formatMessage']
) =>
  Yup.object().shape({
    name: testForForbiddenKeywords({
      path: 'name',
      stringSchema: Yup.string()
        .required(formatMessage(errorMessages.companyNameIsRequired))
        .min(
          MIN_COMPANY_NAME_LENGTH,
          formatMessage(errorMessages.enterAtLeastThreeCharacters)
        ),
      fieldName: messages.companyName,
      formatMessage,
    }),
    logo: Yup.string().optional(),
    brandName: testForForbiddenKeywords({
      path: 'brandName',
      stringSchema: Yup.string()
        .optional()
        .test(
          'brandNameLen',
          formatMessage(errorMessages.enterAtLeastThreeCharacters),
          value => {
            if (!value) return true;
            return value.length >= MIN_COMPANY_NAME_LENGTH;
          }
        ),
      fieldName: messages.brandName,
      formatMessage,
    }),
    size: Yup.string().required(
      formatMessage(errorMessages.thisFieldIsRequired)
    ),
    IndustryId: Yup.string().required(
      formatMessage(errorMessages.thisFieldIsRequired)
    ),
    website: Yup.string().matches(
      /^(((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#-]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?|)$/,
      formatMessage(errorMessages.enterValidWebsite)
    ),
    description: Yup.mixed().optional(),
    CountryCode: Yup.string().required(
      formatMessage(errorMessages.thisFieldIsRequired)
    ),
    CityId: Yup.mixed().when('CountryCode', (countryCode, schema) => {
      const { isHierarchicalLocationEnabled } =
        getFeatureEnabledForCountry(countryCode);
      return isHierarchicalLocationEnabled
        ? schema.optional()
        : schema.required(formatMessage(errorMessages.thisFieldIsRequired));
    }),
    locationProvinceId: Yup.mixed().when(
      'CountryCode',
      (countryCode, schema) => {
        const { isHierarchicalLocationEnabled } =
          getFeatureEnabledForCountry(countryCode);
        return isHierarchicalLocationEnabled &&
          (countryCodeIsIndonesia(countryCode) ||
            countryCodeIsMalaysia(countryCode))
          ? schema.required(formatMessage(errorMessages.thisFieldIsRequired))
          : schema.optional();
      }
    ),
    locationCityId: Yup.mixed().when('CountryCode', (countryCode, schema) => {
      const { isHierarchicalLocationEnabled } =
        getFeatureEnabledForCountry(countryCode);
      return isHierarchicalLocationEnabled
        ? schema
            .required(formatMessage(errorMessages.thisFieldIsRequired))
            .test(
              'location-country-compatibility',
              formatMessage(errorMessages.locationCountryMismatch),
              (value: unknown, context?: Yup.TestContext) => {
                if (!context?.parent) {
                  return true;
                }
                const { CountryCode, selectedLocation } =
                  context.parent as CompanyFormState;
                return isLocationCompatibleWithCountry(
                  CountryCode,
                  selectedLocation
                );
              }
            )
        : schema.optional();
    }),
    address: Yup.string().when('CountryCode', (countryCode, schema) => {
      const { isWorkAddressEnabled } = getFeatureEnabledForCountry(countryCode);
      return isWorkAddressEnabled
        ? schema.required(formatMessage(errorMessages.thisFieldIsRequired))
        : schema.optional();
    }),
    acquisitionReferralCode: Yup.string()
      .optional()
      .matches(referralCodeRegex, {
        excludeEmptyString: true,
        message: formatMessage(errorMessages.invalidReferralCode),
      }),
  });

export const defaultCompanyExistsError = 'default_company_exists_error';
export const defaultBrandNameExistsError = 'default_brand_name_exists_error';
